import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellTitle,
  CellCheckboxGroup,
  CellCommon,
  CellCard,
} from '@root/components/Cell';
import Button from '@root/components/Button';
import './index.less';
import useAdjustDevice from '@root/hooks/subscription/useAdjustDevice';
import PageLoading from '@root/components/PageLoading';
import PageLoadError from '@root/components/PageLoadError';
import Empty from '@root/components/Empty';
import SafeAreaInset from '@root/components/SafeAreaInset';
import { isInIosApp } from '@root/shared';
import { EPayMode } from '@root/constant';

const DetailAdjustDevice = () => {
  const { t } = useTranslation();
  const {
    unBindDevices,
    detail,
    selected,
    loading,
    requesting,
    onSelected,
    onUpdatePackage,
    onCancel,
    getDetail,
    updateDeviceLockStatus,
    onRemove,
    onNewSubscribe,
  } = useAdjustDevice();

  const { maxDevNum, payMode, devList, packageChangeRecord, isUnlimited } =
    detail;
  const bindDevLen = devList ? devList.length : 0;

  const options = useMemo(() => {
    return unBindDevices.map((i) => ({
      title: i.deviceName,
      value: i.deviceId,
    }));
  }, [unBindDevices]);

  if (loading === 0) {
    return <PageLoading />;
  }

  if (loading === 2) {
    return <PageLoadError onTryAgain={getDetail} />;
  }

  const isPalPalOrder = payMode !== EPayMode.ApplePay;
  const isBindMax = !isUnlimited && bindDevLen >= maxDevNum;

  return (
    <>
      <div className="flex-section-auto adjust-device-card">
        <CellTitle enhance>
          {t('device_management')}
          {isUnlimited
            ? ` (${t('unlimited_devices')})`
            : maxDevNum > 1
            ? t('device_management_max', { maxDevNum })
            : t('device_management_max_one', { maxDevNum })}
        </CellTitle>
        <CellTitle paddingTop={4}>{t('subscript_bind_devices')}</CellTitle>
        {devList?.length ? (
          <CellCard marginTop={0} marginBottom={0}>
            {devList.map((d) => {
              return (
                <CellCommon inner key={d.deviceId} title={d.deviceName}>
                  {isPalPalOrder && packageChangeRecord !== null && (
                    <>
                      {d.lockStatus ? (
                        <span
                          className="a-link"
                          onClick={() => updateDeviceLockStatus(d.deviceId, 0)}
                        >
                          {t('remove_from_new_plan')}
                        </span>
                      ) : (
                        <span
                          className="a-link"
                          onClick={() => updateDeviceLockStatus(d.deviceId, 1)}
                        >
                          {t('add_to_new_plan')}
                        </span>
                      )}
                    </>
                  )}
                </CellCommon>
              );
            })}
          </CellCard>
        ) : (
          <Empty noIcon>{t('no_data')}</Empty>
        )}
        {/* 是否还有绑定套餐 */}
        {isBindMax ? (
          <p className="service-max-tips">{t('service_max_tips')}</p>
        ) : (
          <>
            <CellTitle>{t('device_unbind')}</CellTitle>
            {options.length ? (
              <CellCheckboxGroup
                options={options}
                values={selected}
                onChange={onSelected}
                maxLength={isUnlimited ? undefined : maxDevNum - bindDevLen}
                marginTop={0}
                marginBottom={0}
              />
            ) : (
              <Empty noIcon>{t('device_unselect')}</Empty>
            )}
          </>
        )}
        {bindDevLen > 0 && (
          <CellTitle>
            <p className="remove-tips">
              {t('service_upper_limit_tips')}
              <span className="a-link remove" onClick={onRemove}>
                {t('remove_device')}
              </span>
              {isPalPalOrder && !isInIosApp && (
                <>
                  {t('or')}
                  <span className="a-link" onClick={onNewSubscribe}>
                    {t('subscribe_new_plan')}
                  </span>
                </>
              )}
            </p>
          </CellTitle>
        )}
      </div>
      <div className="flex-section-footer">
        {!isBindMax && options.length > 0 && (
          <Button
            disabled={selected.length === 0}
            loading={requesting}
            onClick={onUpdatePackage}
          >
            {t('update')}
          </Button>
        )}
        <Button disabled={requesting} onClick={onCancel} color="surface">
          {t('cancel')}
        </Button>
        <SafeAreaInset />
      </div>
    </>
  );
};

export default DetailAdjustDevice;
